import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import FamilyCreatedConfirmationModal from "../CreateNewFamily/FamilyCreatedConfirmationModal";
import { selectUserToken } from "../../../../../../features/userSlice";
import { familiesEndPointLogic } from "../../../../../../logic/DataCollection/Families/FamiliesEndPointLogic";
import {
  buildCandidatesFromCoverageRow,
  buildCandidatesFromThings,
  formatCandidateLabel,
  enrichCandidates,
  type CandidateItem,
  type CoverageRow,
} from "../../../shared/candidateCatalogue";
import {
  CREATE_FAMILY_SUBMIT_TRACKER,
  CREATE_FAMILY_CANCEL_TRACKER,
  EDIT_FAMILY_DETACH_TRACKER,
  EDIT_FAMILY_ATTACH_TRACKER,
  EDIT_FAMILY_SAVE_TRACKER,
  EDIT_FAMILY_CANCEL_TRACKER,
} from "../../../shared/familyActionTrackers";

interface Props {
  open: boolean;
  toggleDrawerClose: () => void;
  familiesTitle: string;
  type: string;
  interfaceName: string;
  stepKey?: "fetcher" | "unifier" | "advisor";
  backendInterfaceId?: string | number;
  interfaceDetail?: any; // Q1 data — enriches the candidate list (see candidateCatalogue.ts)
  coverageRow?: CoverageRow | null; // Q2 row for this interface — the candidate catalogue
  sourceFamily?: any; // present = Duplicate mode instead of Create
  onCreated?: () => void;
}

const isDestinationCovered = (item: CandidateItem, stagedItems: CandidateItem[]) =>
  !!item.destination_table &&
  stagedItems.some((s) => s.destination_table === item.destination_table);

export default function FamilyDrawer({
  open,
  familiesTitle,
  type,
  interfaceName,
  stepKey,
  backendInterfaceId,
  interfaceDetail,
  coverageRow,
  sourceFamily,
  onCreated,
  toggleDrawerClose,
}: Props) {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const isDuplicate = !!sourceFamily;

  const [openModal, setOpenModal] = React.useState(false);
  // Captured at creation time — `sourceFamily` (and therefore `isDuplicate`)
  // gets cleared by toggleDrawerClose() right before this modal opens, so
  // the live value would flip the title back to "Create" as soon as it renders.
  const [wasDuplicate, setWasDuplicate] = React.useState(false);
  const [createdItems, setCreatedItems] = React.useState<CandidateItem[]>([]);
  const [name, setName] = React.useState("");
  const [nameTouched, setNameTouched] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [selectedIds, setSelectedIds] = React.useState<(string | number)[]>([]);
  const [replacingId, setReplacingId] = React.useState<string | number | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [nameError, setNameError] = React.useState("");

  const candidates = React.useMemo(() => {
    if (!stepKey) return [];
    const thingsForStep = (interfaceDetail?.steps?.[stepKey]?.families ?? []).flatMap(
      (f: any) => f.things ?? [],
    );
    return enrichCandidates(
      buildCandidatesFromCoverageRow(coverageRow, stepKey),
      buildCandidatesFromThings(thingsForStep),
    );
  }, [stepKey, interfaceDetail, coverageRow]);

  // Reset only when the drawer opens (or its source changes) — not when
  // `candidates` changes, since Q2 can land after the drawer is already open
  // and that must not wipe what the user has typed/picked.
  const hasPrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) return;
    setName(
      sourceFamily?.ui_family_name ? `${sourceFamily.ui_family_name} (copy)` : "",
    );
    setSelectedIds([]);
    hasPrefilledRef.current = false;
    setSearch("");
    setNameError("");
    setNameTouched(false);
    setReplacingId(null);
  }, [open, sourceFamily]);

  // Duplicate mode prefills from the source family's real things, matched
  // against the candidate list by id (falls back to name in case a
  // candidate's id format differs slightly between contexts). Runs once,
  // as soon as candidates are available.
  React.useEffect(() => {
    if (!open || !sourceFamily || hasPrefilledRef.current || !candidates.length) return;
    const sourceIds = (sourceFamily.things ?? []).map((t: any) =>
      String(t.config_id ?? t.map_id),
    );
    const sourceNames = (sourceFamily.things ?? []).map((t: any) =>
      (t.configuration_name ?? t.map_name ?? "").toLowerCase(),
    );
    setSelectedIds(
      candidates
        .filter(
          (c) => sourceIds.includes(String(c.id)) || sourceNames.includes(c.name.toLowerCase()),
        )
        .map((c) => c.id),
    );
    hasPrefilledRef.current = true;
  }, [open, sourceFamily, candidates]);

  const resetForm = () => {
    setName("");
    setSearch("");
    setSelectedIds([]);
    setNameError("");
    setNameTouched(false);
    setReplacingId(null);
  };

  const handleClose = () => {
    resetForm();
    toggleDrawerClose();
  };

  const staged = candidates.filter((item) => selectedIds.some((id) => String(id) === String(item.id)));

  const handleCreateFamily = async () => {
    const trimmedName = name.trim();
    // The real API requires ui_family_name on create — despite the spec doc
    // describing it as optional with backend fallback naming, live testing
    // confirmed the endpoint 400s with "ui_family_name is required" when omitted.
    if (!trimmedName) {
      setNameError("Family name is required.");
      return;
    }
    setNameError("");
    setIsSubmitting(true);
    const payload: any = {
      step: stepKey,
      backend_interface_id: backendInterfaceId,
      map_ids: selectedIds,
      ui_family_name: trimmedName,
    };

    const { success, message } = await familiesEndPointLogic.createFamily(
      oauthTokenHeader,
      payload,
      dispatch,
      isDuplicate ? "Family duplicated successfully" : "Family created successfully",
    );
    setIsSubmitting(false);

    if (!success) {
      // Snackbar already shown by the logic layer; additionally mirror the
      // message onto the name field when it looks name-related, since the
      // codebase has no general inline-field-error convention for this.
      if (message && /name/i.test(message)) setNameError(message);
      return; // stays open with the current selections intact
    }

    setCreatedItems(staged);
    setWasDuplicate(isDuplicate);
    resetForm();
    toggleDrawerClose();
    onCreated?.();
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const stageCandidate = (item: CandidateItem) => {
    if (replacingId) {
      setSelectedIds((prev) =>
        prev.map((id) => (String(id) === String(replacingId) ? item.id : id)),
      );
      setReplacingId(null);
    } else {
      setSelectedIds((prev) =>
        prev.some((id) => String(id) === String(item.id)) ? prev : [...prev, item.id],
      );
    }
    setSearch("");
  };

  const handleSwitchClick = (id: string | number) => {
    setReplacingId(id);
    setSearch("");
  };

  const handleDetachClick = (id: string | number) => {
    setSelectedIds((prev) => prev.filter((x) => String(x) !== String(id)));
    if (String(replacingId) === String(id)) setReplacingId(null);
  };

  const isFetcher = type.toLowerCase() === "configs";
  const remaining = candidates.filter(
    (item) =>
      !selectedIds.some((id) => String(id) === String(item.id)) &&
      formatCandidateLabel(item).toLowerCase().includes(search.toLowerCase()) &&
      (isFetcher || !isDestinationCovered(item, staged)),
  );

  const thingsLower = type.toLowerCase();
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  const actionLabel = isDuplicate ? "Duplicate" : "Create";
  // Create and Duplicate share this drawer but track under different action
  // groups — Detach/Attach are only tracked in Duplicate mode, matching Edit.
  const safeStepKey = stepKey ?? "fetcher";
  const submitTracker = isDuplicate
    ? EDIT_FAMILY_SAVE_TRACKER[safeStepKey]
    : CREATE_FAMILY_SUBMIT_TRACKER[safeStepKey];
  const cancelTracker = isDuplicate
    ? EDIT_FAMILY_CANCEL_TRACKER[safeStepKey]
    : CREATE_FAMILY_CANCEL_TRACKER[safeStepKey];
  const detachTracker = isDuplicate ? EDIT_FAMILY_DETACH_TRACKER[safeStepKey] : undefined;
  const attachTracker = isDuplicate ? EDIT_FAMILY_ATTACH_TRACKER[safeStepKey] : undefined;
  const replacingItem = replacingId
    ? staged.find((c) => String(c.id) === String(replacingId))
    : null;

  return (
    <div>
      <Drawer
        anchor="right"
        open={open}
        sx={{ zIndex: 1300 }}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          handleClose();
        }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
      >
        <Box sx={{ p: 3, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
              {actionLabel} New {familiesTitle} Family
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 1.5 }}>
              Family Details
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#00000099" }}>Name*</Typography>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Insert a unique Family Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameTouched(true);
                if (nameError) setNameError("");
              }}
              color="secondary"
              error={!!nameError}
            />
            <Typography sx={{ fontSize: 12, color: nameError ? "#D32F2F" : "#00000061", mt: 0.5 }}>
              {nameError ||
                (nameTouched
                  ? "This field is mandatory"
                  : `Unique name within ${interfaceName} · ${familiesTitle}. This field is compulsory.`)}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 1.5 }}>
              Select {typeLabel}
            </Typography>

            {replacingItem && (
              <Typography sx={{ fontSize: 12, color: "#9C27B0", mb: 0.5 }}>
                Switching &quot;{replacingItem.name}&quot; — pick a replacement below.{" "}
                <Box
                  component="span"
                  onClick={() => setReplacingId(null)}
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  Cancel
                </Box>
              </Typography>
            )}

            <Autocomplete
              size="small"
              options={remaining}
              inputValue={search}
              onInputChange={(_, val) => setSearch(val)}
              onChange={(_, val) => {
                if (val) stageCandidate(val);
              }}
              getOptionLabel={formatCandidateLabel}
              filterOptions={(opts) => opts}
              noOptionsText={`No available ${thingsLower}.`}
              renderOption={(props, item) => (
                <Box component="li" {...props} key={item.id} data-tracker={attachTracker}>
                  <Box>
                    <Typography sx={{ color: "#9C27B0", fontSize: 14, fontWeight: 500 }}>
                      {item.name}{" "}
                      <Box component="span" sx={{ color: "#00000061", fontSize: 12, fontWeight: 500 }}>
                        ({item.id})
                      </Box>
                    </Typography>
                    <Typography sx={{ color: "#00000099", fontSize: 12 }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Search ${typeLabel}`}
                  color="secondary"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ fontSize: 20, color: "#666", mr: 0.5 }} />,
                  }}
                />
              )}
            />
            <Typography sx={{ fontSize: 12, color: "#00000061", mt: 0.5 }}>
              You can select {thingsLower} now or later.
            </Typography>

            {staged.length > 0 && (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {staged.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #0000001F",
                      borderRadius: "8px",
                      p: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: "#9C27B0", fontSize: 14, fontWeight: 500 }}>
                        {item.name}{" "}
                        <Box component="span" sx={{ color: "#00000061", fontSize: 12, fontWeight: 500 }}>
                          ({item.id})
                        </Box>
                      </Typography>
                      <Typography sx={{ color: "#00000099", fontSize: 12 }}>
                        {item.description}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} flexShrink={0}>
                      <IconButton size="small" onClick={() => handleSwitchClick(item.id)} sx={{ color: "#9C27B0" }}>
                        <SwapHorizIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDetachClick(item.id)}
                        data-tracker={detachTracker}
                        sx={{ color: "#9C27B0" }}
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button variant="text" color="secondary" onClick={handleClose} data-tracker={cancelTracker}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCreateFamily}
              disabled={!name.trim() || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              data-tracker={submitTracker}
            >
              {actionLabel} New Family
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <FamilyCreatedConfirmationModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        typeLabel={typeLabel}
        items={createdItems}
        isDuplicate={wasDuplicate}
      />
    </div>
  );
}
